import React, { useEffect, useRef, useState } from "react";
import PlusIcon from "assets/icon/PlusIcon";
import { useNavigate } from "react-router-dom";
import { DropdownStatusList, FormTypeAdd, FormTypeEdit, PageSize } from "utils/Constant";
import Dropdown from "components/Input/Dropdown";
import { DateFormat, DateShort, GetStatus, money } from "utils/helpers"
import { useModal } from "context/ModalContext";
import { FilterCard, MainBodyCard, PagesTitle } from "components/Cards";
import Pagination from "components/Input/PaginationButton";
import GridEditButton from "components/Button/GridEditButton";
import GridDeleteButton from "components/Button/GridDeleteButton";
import DataTable from "components/DataTable";
import DatePicker from "components/Input/DatePicker";
import { ExpenseCategoryService } from "services/ExpenseCategory/ExpenseCategoryService";
import { ExpenseService } from "services/Expense/ExpenseService";
import Select from 'react-select';
import AddButton from "components/Button/AddButton";

export default function Expenses(){
    let navigate = useNavigate()

    const debounceRef = useRef(null)
    const firstLoad = useRef(true)
    
    const { showFail, showLoading, hideLoading, showSuccess, confirm } = useModal()
    const { fetchExpenseCategoryList } = ExpenseCategoryService()
    const { fetchExpenseList, fetchDeleteExpense } = ExpenseService()

    const [dateFrom, setDateFrom] = useState(new Date())
    const [dateTo, setDateTo] = useState(new Date())
    const [expenseCategoryOptions, setExpenseCategoryOptions] = useState([])
    const [expenseCategoryId, setExpenseCategoryId] = useState('')
    const [description, setDescription] = useState('')
    const [status, setStatus] = useState(DropdownStatusList[1].value)
    const [pageIndex, setPageIndex] = useState(0)
    const [totalPages, setTotalPages] = useState(0)

    const [expenseList, setExpenseList] = useState([])

    const columns = [
        {
            key: "rowNumber",
            header: "No",
            headerClassName: "w-16 font-semibold",
            cellClassName: "text-center",
        },
        {
            key: "expenseDate",
            header: "Tanggal",
            render: (row) => DateShort(row.expenseDate),
        },
        {
            key: "expenseCategoryName",
            header: "Kategori",
        },
        {
            key: "description",
            header: "Deskripsi",
        },
        {
            key: "amount",
            header: "Jumlah",
            render: (row) => 'Rp ' + money(row.amount),
        },
        {
            key: "status",
            header: "Status",
            render: (row) => GetStatus(row.status),
        },
        {
            key: "action",
            header: "Action",
            render: (row) => (
                <div className="flex gap-2">
                    <GridEditButton isActive={row.status} onClick={() => editBtnClick(row.expenseId)} size='w-3' />
                    <GridDeleteButton isActive={row.status} onClick={() => deleteBtnClick(row.expenseId, row.status)} size="w-3" />
                </div>                
            ),
        },
    ]

    // Function Grid
    const handlePageChange = async (newIndex) => {
        const res = await fetchExpenseList(DateFormat(dateFrom), DateFormat(dateTo), expenseCategoryId || 0, description, status, PageSize, newIndex)
        if (!res) return
        
        const totalData = res?.data?.totalData ?? 0;
        const total = Math.max(1, Math.ceil(totalData / PageSize))

        setExpenseList(res)
        setTotalPages(total)
        setPageIndex(newIndex)
    }

    // Button Event
    const addBtnClick = () => {
        navigate("/expenses/form?expenseId=&formType=" + FormTypeAdd)
    }
    const editBtnClick = (id) => {
        navigate("/expenses/form?expenseId=" + id + "&formType=" + FormTypeEdit)
    }
    const deleteBtnClick = async (id, status) => {
        const onConfirm = await confirm()
        if (!onConfirm) return

        showLoading()

        try {
            await deleteExpense(id, status)

            showSuccess({
                onSuccess: async () => {
                    await handlePageChange(pageIndex)
                }
            })
        }
        finally {
            hideLoading()
        }
    }

    const deleteExpense = async (id, status) => {
        const res = await fetchDeleteExpense(id, status)
        if (!res) return 
    }
    const getExpenseCategory = async () => {
        const res = await fetchExpenseCategoryList('', '1', 100, 0)
        if (!res) return

        const data = res?.data?.listData
        const options = [
            { label: "All", value: "" },
            ...data.map((x) => ({
                label: x.expenseCategoryName,
                value: String(x.expenseCategoryId),
            })),
        ];

        setExpenseCategoryOptions(options)
    }

    useEffect(() => {
        const onLoad = async () => {
            showLoading()

            try {
                if (firstLoad.current) {
                    await getExpenseCategory()
                    await handlePageChange(0)

                    firstLoad.current = false
                } else {
                    if (debounceRef.current) {
                        clearTimeout(debounceRef.current)
                    }
            
                    debounceRef.current = setTimeout(async () => {
                        await handlePageChange(0)
                    }, 500)
                }
            }
            finally {
                hideLoading()
            }
        }

        onLoad()
    },[dateFrom, dateTo, expenseCategoryId, description, status])

    return(
        <MainBodyCard>
            <PagesTitle title='Pengeluaran' />

            <div className="flex flex-col sm:flex-row justify-between gap-8 mt-5">
                <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-4 w-full">
                    <FilterCard title='Date From' width='sm:w-52 w-full'>
                        <DatePicker value={dateFrom} onChange={setDateFrom} disableClock={true} />
                    </FilterCard>

                    <FilterCard title='Date To' width='sm:w-52 w-full'>
                        <DatePicker value={dateTo} onChange={setDateTo} disableClock={true} />
                    </FilterCard>

                    <FilterCard title='Category' width='sm:w-56 w-full'>
                        <Select options={expenseCategoryOptions} value={expenseCategoryOptions.find((x) => x.value === expenseCategoryId)} onChange={(e) => { setExpenseCategoryId(e.value) }} placeholder='All' />
                    </FilterCard>

                    <FilterCard title='Description' width='sm:w-72 w-full'>
                        <input className="input" value={description} onChange={(e) => { setDescription(e.target.value) }} maxLength={40} />
                    </FilterCard>

                    <FilterCard title='Status' width='sm:w-32 w-full'>
                        <Dropdown value={status} options={DropdownStatusList} onChange={(e) => { setStatus(e.value) }} placeholder='All' />
                    </FilterCard>
                </div>
            </div>

            <div className="flex w-full justify-end mt-4">
                <AddButton onClick={addBtnClick} text={'Tambah Pengeluaran'} />
            </div>

            <div className="mt-5 hidden sm:block">
                <DataTable
                    columns={columns}
                    data={expenseList?.data?.listData ?? []}
                    footer={
                        <tr className="h-10">
                            <td colSpan={4} className="text-right px-2">
                                <span className="font-semibold">Total</span>
                            </td>
                            <td colSpan={3} className="px-2">
                                <span className="font-semibold">
                                    Rp {money(String(expenseList?.data?.totalAmount))}
                                </span>
                            </td>
                        </tr>
                    }
                />
            </div>

            <div className="sm:hidden mt-4 flex flex-col gap-3">
            {
                expenseList?.data?.listData?.length === 0 ? (
                    <div className="text-center h-12">
                        <span>No Data Available</span> 
                    </div>
                ) :
                
                expenseList?.data?.listData?.map((data, i) => {
                    return(
                        <div className="p-2 border rounded-lg hover:bg-slate-100" key={ i } onClick={() => {editBtnClick(data.expenseId)}}>
                            <div className="flex justify-between">
                                <span>#{data.expenseId} </span>
                                <span>{DateShort(data.expenseDate)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>{data.expenseCategoryName}</span>
                                <span>Rp {money(data.amount)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>{data.description}</span>
                                <span>{}</span>
                            </div>
                        </div>
                    )
                })
            }
            </div>

            <Pagination
                pageIndex={pageIndex}
                totalPages={totalPages}
                onPageChange={handlePageChange}
            />
        </MainBodyCard>
    )
}